import React, { useState, useMemo } from 'react';

/**
 * Binary Game Decision Tree Engine
 * 
 * A forward-only, user-driven state machine for interactive decision trees.
 * Follows the five hard rules:
 * 1. One active gate at a time
 * 2. Forward only (no backward navigation)
 * 3. Sequential unlock (Gate N+1 doesn't exist until Gate N is answered)
 * 4. Animation is gated by state, not by time (no auto-advance)
 * 5. Resolved gates are visible but dead (no interaction)
 */

// Diamond SVG component
const Diamond = ({ color = '#0057FF', size = 'sm' }) => {
  const sizes = {
    xs: { width: 6, height: 10 },
    sm: { width: 8, height: 13 },
    md: { width: 10, height: 16 },
    lg: { width: 11, height: 18 }
  };
  const { width, height } = sizes[size] || sizes.sm;
  
  return (
    <svg 
      width={width} 
      height={height} 
      viewBox="0 0 10 16" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'inline-block', verticalAlign: 'middle' }}
    >
      <polygon points="5,0 10,8 5,16 0,8" fill={color}/>
    </svg>
  );
};

/**
 * GateCard Component
 * Renders a single gate with proper state-based styling and interaction
 */
const GateCard = ({ 
  gate, 
  gateIndex,
  isActive, 
  isResolved, 
  chosenAnswer, 
  onAnswer,
  isExitPoint,
  pathLabel,
  nextGateHasPathLabel
}) => {
  const getYesLabel = () => gate.yesLabel || 'YES';
  const getNoLabel = () => gate.noLabel || 'NO';
  const isThisThat = gate.type === 'THIS/THAT';
  const isSomatic = gate.type === 'SOMATIC';
  const isInverted = gate.inverted === true; // For gates where NO continues and YES exits

  // Get the connector label - shows the actual answer the user chose
  const getConnectorLabel = () => {
    if (chosenAnswer === 'yes') {
      return getYesLabel();
    } else {
      return getNoLabel();
    }
  };

  // For inverted gates: NO is the forward answer (blue button), YES is the exit answer (white button)
  // For normal gates: YES is the forward answer (blue button), NO is the exit answer (white button)
  const primaryAnswer = isInverted ? 'no' : 'yes';
  const secondaryAnswer = isInverted ? 'yes' : 'no';
  const primaryLabel = isInverted ? getNoLabel() : getYesLabel();
  const secondaryLabel = isInverted ? getYesLabel() : getNoLabel();

  return (
    <div className={`lt-gate-section ${isActive ? 'lt-gate-active' : ''} ${isResolved ? 'lt-gate-resolved' : ''}`}>
      {/* Path label if transitioning to a new branch */}
      {pathLabel && (
        <div className="lt-path-label lt-fade-in">
          <div className="lt-line-down" />
          <span className="lt-path-name">{pathLabel}</span>
          <div className="lt-line-down" />
        </div>
      )}

      {/* Gate Card */}
      <div 
        className="lt-gate-card"
        style={isResolved ? { pointerEvents: 'none' } : {}}
      >
        <div className="lt-gate-spine" />
        <div className="lt-gate-content">
          <span className="lt-gate-label">
            GATE {gate.id} | {gate.type}
            {gate.badge && <span className="lt-gate-badge">{gate.badge}</span>}
          </span>
          <p className="lt-gate-question">{gate.question}</p>
          {gate.subtext && <p className="lt-gate-subtext">{gate.subtext}</p>}
          {gate.note && <p className="lt-gate-note">{gate.note}</p>}
          
          {/* Buttons - order and styling swapped for inverted gates */}
          <div className="lt-gate-buttons">
            <button
              className={`lt-btn lt-btn-yes ${isResolved && chosenAnswer !== primaryAnswer ? 'lt-btn-dim' : ''}`}
              onClick={() => onAnswer(gateIndex, primaryAnswer)}
              disabled={isResolved}
            >
              {!isThisThat && !isSomatic && !isInverted && <Diamond color="#FFFFFF" size="xs" />}
              {primaryLabel}
            </button>
            <button
              className={`lt-btn lt-btn-no ${isResolved && chosenAnswer !== secondaryAnswer ? 'lt-btn-dim' : ''}`}
              onClick={() => onAnswer(gateIndex, secondaryAnswer)}
              disabled={isResolved}
            >
              {secondaryLabel}
            </button>
          </div>
          
          {gate.footnote && isResolved && (
            <p className="lt-gate-footnote">{gate.footnote}</p>
          )}
        </div>
      </div>

      {/* Connector to next gate - line segment + label + line segment */}
      {isResolved && !isExitPoint && (
        <div className="lt-connector lt-fade-in">
          <div className="lt-line-down" />
          {!nextGateHasPathLabel && (
            <span className="lt-connector-label">{getConnectorLabel()}</span>
          )}
          <div className="lt-line-down" />
        </div>
      )}
    </div>
  );
};

/**
 * DecisionTreeEngine Component
 * 
 * Props:
 * - treeConfig: { title, subtitle, domain?, identityQuestion?, result/results }
 * - getNextGates: (currentGateIndex, answers) => [{ id, type, question, ... }, ...]
 *   Returns the remaining gates in sequence based on current state.
 *   The engine walks through these one at a time, forward only.
 * - getResult: (answers) => { text, type? } - Returns the final result based on answers
 * - onClose: () => void - Called when close button is clicked
 */
const DecisionTreeEngine = ({ treeConfig, getNextGates, getResult, onClose }) => {
  const [currentGateIndex, setCurrentGateIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [exitedAtIndex, setExitedAtIndex] = useState(null);
  
  // Get the current sequence of gates based on answers so far
  const gates = useMemo(() => {
    return getNextGates(currentGateIndex, answers);
  }, [currentGateIndex, answers, getNextGates]);

  // Determine if tree is complete (all gates answered with continuing answers)
  const isComplete = currentGateIndex >= gates.length && exitedAtIndex === null;
  const isExited = exitedAtIndex !== null;

  // Handle answer - the only state transition
  const handleAnswer = (gateIndex, answer) => {
    // Rule 1: Only accept answers from the active gate
    if (gateIndex !== currentGateIndex) return;
    
    const gate = gates[gateIndex];
    
    // Record the answer
    setAnswers(prev => ({ ...prev, [gate.id]: answer }));

    // Check if this answer exits the tree
    // For inverted gates, YES exits instead of NO
    const isInverted = gate.inverted === true;
    const exitAnswer = isInverted ? 'yes' : 'no';
    const isExitAnswer = answer === exitAnswer && !gate.continuesBoth;
    
    if (isExitAnswer) {
      setExitedAtIndex(gateIndex);
    } else {
      // Rule 2 & 3: Move forward by exactly 1
      setCurrentGateIndex(prev => prev + 1);
    }
  };

  // Reset the tree
  const reset = () => {
    setCurrentGateIndex(0);
    setAnswers({});
    setExitedAtIndex(null);
  };

  // Get result when complete
  const result = isComplete ? getResult(answers) : null;

  return (
    <div className="lt-overlay">
      <div className="lt-container">
        {/* Close button */}
        <button className="lt-close" onClick={onClose} aria-label="Close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>

        {/* Header */}
        <div className="lt-header">
          {treeConfig.domain && <p className="lt-domain">{treeConfig.domain}</p>}
          <h1 className="lt-title">
            <Diamond color="#0057FF" size="md" /> {treeConfig.title}
          </h1>
          <p className="lt-subtitle">{treeConfig.subtitle}</p>
          {treeConfig.identityQuestion && (
            <p className="lt-identity-q">Identity question: {treeConfig.identityQuestion}</p>
          )}
        </div>

        {/* Tree Canvas */}
        <div className="lt-canvas">
          {/* START Node */}
          <div className="lt-start">START</div>
          <div className="lt-line-down" />

          {/* Gates - only render up to currentGateIndex (or exitedAtIndex if exited) */}
          {gates.map((gate, index) => {
            const maxVisibleIndex = exitedAtIndex !== null ? exitedAtIndex : currentGateIndex;
            
            // Rule 3: Unrevealed gates do not exist in the DOM
            if (index > maxVisibleIndex) return null;

            const isActive = index === currentGateIndex && exitedAtIndex === null;
            const isResolved = index < currentGateIndex || (exitedAtIndex !== null && index <= exitedAtIndex);
            const isExitPoint = exitedAtIndex === index;
            const chosenAnswer = answers[gate.id];
            
            // Check if the next gate has a path label (to suppress connector label)
            const nextGate = gates[index + 1];
            const nextGateHasPathLabel = nextGate && nextGate.pathLabel;

            return (
              <GateCard
                key={`${gate.id}-${index}`}
                gate={gate}
                gateIndex={index}
                isActive={isActive}
                isResolved={isResolved}
                chosenAnswer={chosenAnswer}
                onAnswer={handleAnswer}
                isExitPoint={isExitPoint}
                pathLabel={gate.pathLabel}
                nextGateHasPathLabel={nextGateHasPathLabel}
              />
            );
          })}

          {/* Result Node - shown when tree is completed */}
          {isComplete && result && (
            <div className="lt-result-section lt-fade-in">
              <div className="lt-result">
                <Diamond color="#0057FF" size="lg" />
                <p className="lt-result-text">{result.text}</p>
              </div>
            </div>
          )}

          {/* Exit Result - shown when user chose to exit */}
          {isExited && exitedAtIndex !== null && (
            <div className="lt-exit-result lt-fade-in">
              <div className="lt-connector">
                <div className="lt-line-down" />
                <span className="lt-connector-label">{answers[gates[exitedAtIndex].id] === 'yes' ? (gates[exitedAtIndex].yesLabel || 'YES') : (gates[exitedAtIndex].noLabel || 'NO')}</span>
                <div className="lt-line-down" />
              </div>
              <div className="lt-exit-card">
                <p className="lt-exit-text">{gates[exitedAtIndex].noText}</p>
              </div>
            </div>
          )}

          {/* Run Again Button */}
          {(isComplete || isExited) && (
            <button className="lt-reset lt-fade-in" onClick={reset}>
              RUN AGAIN
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DecisionTreeEngine;
